'use client';

import { useParams } from 'next/navigation';
import { getEventById } from '@/lib/mock-data';
import { EventDetailSkeleton } from '@/components/LoadingSkeleton';
import { PackageTierCard } from '@/components/PackageTierCard';
import { Calendar, MapPin, Users, Clock, Shield, Award } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { EmptyState } from '@/components/EmptyState';

export default function EventDetailPage() {
  const params = useParams();
  const eventId = params.id as string;
  const event = getEventById(eventId);

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
        <div className="mx-auto max-w-7xl">
          <EmptyState
            title="Event Not Found"
            description="We couldn't find the event you're looking for. It may have been removed or the link is incorrect."
            actionLabel="Browse All Events"
            actionHref="/events"
          />
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const getEventTime = (dateString: string) => {
    // Mock time - in real app this would come from event data
    return '7:00 PM';
  };

  const isLowAvailability = event.ticketsRemaining < 10;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F]">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <Image
          src={event.imageUrl}
          alt={event.eventTitle}
          fill
          className="object-cover"
          priority
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/80 to-transparent" />

        {/* Content overlay */}
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-7xl px-4 pb-12">
            {/* Breadcrumb */}
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-400">
              <Link href="/events" className="hover:text-[#D4AF37]">
                Events
              </Link>
              <span>/</span>
              <span className="text-white">{event.artistName}</span>
            </div>

            <h1 className="mb-4 font-serif text-5xl font-bold text-white md:text-6xl">
              {event.artistName}
            </h1>
            <p className="mb-6 text-2xl text-gray-300">{event.eventTitle}</p>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-6 text-white">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#D4AF37]" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#D4AF37]" />
                <span>{getEventTime(event.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#D4AF37]" />
                <span>
                  {event.venue}, {event.city}
                </span>
              </div>
              {isLowAvailability && (
                <div className="flex items-center gap-2 rounded-full bg-red-500/20 px-4 py-1 text-red-400">
                  <Users className="h-4 w-4" />
                  <span className="font-bold">Only {event.ticketsRemaining} tickets left!</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-16">
        {/* Event Description */}
        <div className="mb-16">
          <h2 className="mb-6 font-serif text-3xl font-bold text-white">About This Event</h2>
          <p className="max-w-4xl text-lg leading-relaxed text-gray-300">{event.description}</p>
        </div>

        {/* Why VIP Section */}
        <div className="mb-16 rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-8 backdrop-blur-md">
          <h2 className="mb-8 text-center font-serif text-3xl font-bold text-white">
            Why Choose{' '}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#B76E79] bg-clip-text text-transparent">
              VIP Experience
            </span>
          </h2>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-lg border border-[#D4AF37]/10 bg-black/40 p-6 text-center">
              <Shield className="mx-auto mb-4 h-12 w-12 text-[#D4AF37]" />
              <h3 className="mb-2 font-serif text-xl font-bold text-white">Premium Access</h3>
              <p className="text-sm text-gray-400">
                Skip the lines with fast-track entry and exclusive VIP lounge access
              </p>
            </div>

            <div className="rounded-lg border border-[#D4AF37]/10 bg-black/40 p-6 text-center">
              <Award className="mx-auto mb-4 h-12 w-12 text-[#D4AF37]" />
              <h3 className="mb-2 font-serif text-xl font-bold text-white">Best Seats</h3>
              <p className="text-sm text-gray-400">
                Front row and premium seating zones for the ultimate view
              </p>
            </div>

            <div className="rounded-lg border border-[#D4AF37]/10 bg-black/40 p-6 text-center">
              <Users className="mx-auto mb-4 h-12 w-12 text-[#D4AF37]" />
              <h3 className="mb-2 font-serif text-xl font-bold text-white">Meet & Greet</h3>
              <p className="text-sm text-gray-400">
                Exclusive backstage access and photo opportunities with the artist
              </p>
            </div>
          </div>
        </div>

        {/* VIP Packages Section */}
        <div>
          <div className="mb-8 text-center">
            <h2 className="mb-4 font-serif text-4xl font-bold text-white">
              Choose Your{' '}
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#B76E79] bg-clip-text text-transparent">
                VIP Package
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-gray-400">
              Select the perfect package for your unforgettable experience. All packages include
              premium amenities and exclusive perks.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {event.packages.map((pkg, index) => (
              <PackageTierCard
                key={pkg.id}
                pkg={pkg}
                eventId={event.id}
                featured={pkg.tier === 'Platinum'}
              />
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 rounded-xl border border-[#D4AF37]/20 bg-gradient-to-r from-black/60 to-black/40 p-8 backdrop-blur-md">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="text-center md:text-left">
              <h3 className="mb-2 font-serif text-2xl font-bold text-white">
                Secure & Trusted Booking
              </h3>
              <p className="text-gray-400">
                USDT cryptocurrency payments • Instant confirmation • 24/7 support
              </p>
            </div>
            <div className="flex gap-8">
              <div className="text-center">
                <p className="mb-1 font-serif text-3xl font-bold text-[#D4AF37]">2,000+</p>
                <p className="text-sm text-gray-400">Happy Guests</p>
              </div>
              <div className="h-16 w-px bg-[#D4AF37]/20"></div>
              <div className="text-center">
                <p className="mb-1 font-serif text-3xl font-bold text-[#D4AF37]">50+</p>
                <p className="text-sm text-gray-400">VIP Events</p>
              </div>
              <div className="h-16 w-px bg-[#D4AF37]/20"></div>
              <div className="text-center">
                <p className="mb-1 font-serif text-3xl font-bold text-[#D4AF37]">5★</p>
                <p className="text-sm text-gray-400">Rating</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
